// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useDashboardApi } from "../../store/api/hooks/dashboardApiHook";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  LabelList,
} from "recharts";
import Layout from "../../components/layout";
import Header from "../../components/header";
import { downloadExcel, downloadPDF } from "../../_common/functions";
import { Dropdown, Menu } from "../../components/Dropdown";

// ─── Types ───────────────────────────────────────────────────────────────────

type GlobalTimePeriod = "1W" | "3W" | "30D" | "3Mo" | "6Mo" | "12Mo";

interface FacilityRow {
  facility: string;
  [user: string]: string | number;
}

interface RoleRow {
  role: string;
  [user: string]: string | number;
}

type ActivityRow = { user: string; Hours: number; Observations: number; "Asset Moves": number; "WRs Created": number; "Tasks Completed": number; "Activities Logged": number };
type ActivityMetricKey = "Hours" | "Observations" | "Asset Moves" | "WRs Created" | "Tasks Completed" | "Activities Logged";

interface StockPickUser { user: string; count: number }
interface StockPickRow {
  week: string;
  Picking: number;
  pickingDollars: number;
  pickingUsers: StockPickUser[];
  Returned: number;
  returnedDollars: number;
  returnedUsers: StockPickUser[];
  Used: number;
  usedDollars: number;
  usedUsers: StockPickUser[];
}

type StockPickType = "Picking" | "Returned" | "Used";

// ─── Static data ─────────────────────────────────────────────────────────────

const globalPeriodLabels: Record<GlobalTimePeriod, string> = {
  "1W": "Last 1 week",
  "3W": "Last 3 weeks",
  "30D": "Last 30 days",
  "3Mo": "Last 3 months",
  "6Mo": "Last 6 months",
  "12Mo": "Last 12 months",
};

const wrSparklineData = [
  { m: "Jul", v: 240 }, { m: "Aug", v: 280 }, { m: "Sep", v: 310 },
  { m: "Oct", v: 290 }, { m: "Nov", v: 350 }, { m: "Dec", v: 377 },
];


const checklistSparklineData = [
  { m: "Jul", v: 160 }, { m: "Aug", v: 185 }, { m: "Sep", v: 200 },
  { m: "Oct", v: 220 }, { m: "Nov", v: 248 }, { m: "Dec", v: 273 },
];

const donutData = [
  { name: "With Checklist", value: 68, color: "#16a34a" },
  { name: "Without Checklist", value: 32, color: "#e5e7eb" },
];

const wrConversionDonutData = [
  { name: "Converted", value: 73, color: "#16a34a" },
  { name: "Not Converted", value: 27, color: "#e5e7eb" },
];

const workRequestDataByPeriod: Record<GlobalTimePeriod, { week: string; Growers: number; "Live Mgmt": number; "R&M Mgmt": number; "Service Techs": number; "R&M Team": number }[]> = {
  "1W": [
    { week: "Mon", Growers: 4, "Live Mgmt": 3, "R&M Mgmt": 2, "Service Techs": 1, "R&M Team": 2 },
    { week: "Tue", Growers: 5, "Live Mgmt": 2, "R&M Mgmt": 1, "Service Techs": 2, "R&M Team": 3 },
    { week: "Wed", Growers: 3, "Live Mgmt": 4, "R&M Mgmt": 2, "Service Techs": 1, "R&M Team": 2 },
    { week: "Thu", Growers: 6, "Live Mgmt": 2, "R&M Mgmt": 3, "Service Techs": 1, "R&M Team": 3 },
    { week: "Fri", Growers: 4, "Live Mgmt": 3, "R&M Mgmt": 1, "Service Techs": 2, "R&M Team": 2 },
    { week: "Sat", Growers: 2, "Live Mgmt": 1, "R&M Mgmt": 1, "Service Techs": 0, "R&M Team": 1 },
    { week: "Sun", Growers: 1, "Live Mgmt": 0, "R&M Mgmt": 0, "Service Techs": 1, "R&M Team": 1 },
  ],
  "3W": [
    { week: "Week 1", Growers: 15, "Live Mgmt": 14, "R&M Mgmt": 7, "Service Techs": 8, "R&M Team": 9 },
    { week: "Week 2", Growers: 20, "Live Mgmt": 11, "R&M Mgmt": 10, "Service Techs": 4, "R&M Team": 14 },
    { week: "Week 3", Growers: 25, "Live Mgmt": 13, "R&M Mgmt": 6, "Service Techs": 7, "R&M Team": 11 },
  ],
  "30D": [
    { week: "Week 1", Growers: 18, "Live Mgmt": 12, "R&M Mgmt": 8, "Service Techs": 6, "R&M Team": 10 },
    { week: "Week 2", Growers: 22, "Live Mgmt": 10, "R&M Mgmt": 9, "Service Techs": 5, "R&M Team": 12 },
    { week: "Week 3", Growers: 15, "Live Mgmt": 14, "R&M Mgmt": 7, "Service Techs": 8, "R&M Team": 9 },
    { week: "Week 4", Growers: 20, "Live Mgmt": 11, "R&M Mgmt": 10, "Service Techs": 4, "R&M Team": 14 },
  ],
  "3Mo": [
    { week: "Oct", Growers: 78, "Live Mgmt": 45, "R&M Mgmt": 32, "Service Techs": 22, "R&M Team": 40 },
    { week: "Nov", Growers: 85, "Live Mgmt": 50, "R&M Mgmt": 28, "Service Techs": 25, "R&M Team": 45 },
    { week: "Dec", Growers: 92, "Live Mgmt": 48, "R&M Mgmt": 35, "Service Techs": 20, "R&M Team": 42 },
  ],
  "6Mo": [
    { week: "Jul", Growers: 65, "Live Mgmt": 38, "R&M Mgmt": 25, "Service Techs": 18, "R&M Team": 34 },
    { week: "Aug", Growers: 70, "Live Mgmt": 42, "R&M Mgmt": 30, "Service Techs": 20, "R&M Team": 38 },
    { week: "Sep", Growers: 74, "Live Mgmt": 40, "R&M Mgmt": 28, "Service Techs": 24, "R&M Team": 36 },
    { week: "Oct", Growers: 78, "Live Mgmt": 45, "R&M Mgmt": 32, "Service Techs": 22, "R&M Team": 40 },
    { week: "Nov", Growers: 85, "Live Mgmt": 50, "R&M Mgmt": 28, "Service Techs": 25, "R&M Team": 45 },
    { week: "Dec", Growers: 92, "Live Mgmt": 48, "R&M Mgmt": 35, "Service Techs": 20, "R&M Team": 42 },
  ],
  "12Mo": [
    { week: "Jan", Growers: 55, "Live Mgmt": 30, "R&M Mgmt": 20, "Service Techs": 15, "R&M Team": 28 },
    { week: "Feb", Growers: 58, "Live Mgmt": 32, "R&M Mgmt": 22, "Service Techs": 16, "R&M Team": 30 },
    { week: "Mar", Growers: 60, "Live Mgmt": 35, "R&M Mgmt": 24, "Service Techs": 17, "R&M Team": 32 },
    { week: "Apr", Growers: 62, "Live Mgmt": 36, "R&M Mgmt": 23, "Service Techs": 19, "R&M Team": 33 },
    { week: "May", Growers: 64, "Live Mgmt": 37, "R&M Mgmt": 26, "Service Techs": 20, "R&M Team": 35 },
    { week: "Jun", Growers: 63, "Live Mgmt": 38, "R&M Mgmt": 24, "Service Techs": 18, "R&M Team": 34 },
    { week: "Jul", Growers: 65, "Live Mgmt": 38, "R&M Mgmt": 25, "Service Techs": 18, "R&M Team": 34 },
    { week: "Aug", Growers: 70, "Live Mgmt": 42, "R&M Mgmt": 30, "Service Techs": 20, "R&M Team": 38 },
    { week: "Sep", Growers: 74, "Live Mgmt": 40, "R&M Mgmt": 28, "Service Techs": 24, "R&M Team": 36 },
    { week: "Oct", Growers: 78, "Live Mgmt": 45, "R&M Mgmt": 32, "Service Techs": 22, "R&M Team": 40 },
    { week: "Nov", Growers: 85, "Live Mgmt": 50, "R&M Mgmt": 28, "Service Techs": 25, "R&M Team": 45 },
    { week: "Dec", Growers: 92, "Live Mgmt": 48, "R&M Mgmt": 35, "Service Techs": 20, "R&M Team": 42 },
  ],
};

const workRequestColors: Record<string, string> = {
  Growers: "#2d7a4f",
  "Live Mgmt": "#3b82c4",
  "R&M Mgmt": "#e0a040",
  "Service Techs": "#5ba8a0",
  "R&M Team": "#e88c6a",
};

const growerNames = ["Jake R.", "Maria S.", "Tom B.", "Carlos M.", "Anna K.", "Dave L.", "Sam W.", "Lisa P."];
const growerColors: Record<string, string> = {
  "Jake R.": "#2d7a4f",
  "Maria S.": "#3b82c4",
  "Tom B.": "#e0a040",
  "Carlos M.": "#5ba8a0",
  "Anna K.": "#e88c6a",
  "Dave L.": "#7b6fb0",
  "Sam W.": "#c46b8a",
  "Lisa P.": "#4a9065",
};
const growersTaskDataByPeriod: Record<GlobalTimePeriod, FacilityRow[]> = {
  "1W": [
    { facility: "NE Warehouse", "Jake R.": 2, "Maria S.": 0 },
    { facility: "Seahawk Farm LLC", "Maria S.": 3, "Tom B.": 0 },
    { facility: "Bugeater Farm LLC", "Tom B.": 2, "Carlos M.": 0 },
    { facility: "Y24 Farm LLC", "Carlos M.": 2, "Anna K.": 1 },
    { facility: "Bobcat Farm LLC", "Jake R.": 3, "Dave L.": 0 },
    { facility: "Wolfpack Farm LLC", "Anna K.": 1, "Sam W.": 0 },
    { facility: "Tar Heel Farm LLC", "Dave L.": 2, "Lisa P.": 0 },
    { facility: "Wildcat Farm LLC", "Sam W.": 1, "Maria S.": 1 },
  ],
  "3W": [
    { facility: "NE Warehouse", "Jake R.": 8, "Maria S.": 0 },
    { facility: "Seahawk Farm LLC", "Maria S.": 12, "Tom B.": 0 },
    { facility: "Bugeater Farm LLC", "Tom B.": 9, "Carlos M.": 0 },
    { facility: "Y24 Farm LLC", "Carlos M.": 7, "Anna K.": 5 },
    { facility: "Bobcat Farm LLC", "Jake R.": 10, "Dave L.": 0 },
    { facility: "Wolfpack Farm LLC", "Anna K.": 6, "Sam W.": 0 },
    { facility: "Tar Heel Farm LLC", "Dave L.": 8, "Lisa P.": 0 },
    { facility: "Wildcat Farm LLC", "Sam W.": 6, "Maria S.": 3 },
    { facility: "Lancelot Farm LLC", "Lisa P.": 10, "Tom B.": 0 },
    { facility: "Tusk Farm LLC", "Carlos M.": 5, "Anna K.": 4 },
  ],
  "30D": [
    { facility: "NE Warehouse", "Jake R.": 12, "Maria S.": 0 },
    { facility: "Seahawk Farm LLC", "Maria S.": 18, "Tom B.": 0 },
    { facility: "Bugeater Farm LLC", "Tom B.": 14, "Carlos M.": 0 },
    { facility: "Y24 Farm LLC", "Carlos M.": 11, "Anna K.": 8 },
    { facility: "Bobcat Farm LLC", "Jake R.": 16, "Dave L.": 0 },
    { facility: "Wolfpack Farm LLC", "Anna K.": 9, "Sam W.": 0 },
    { facility: "Tar Heel Farm LLC", "Dave L.": 13, "Lisa P.": 0 },
    { facility: "Wildcat Farm LLC", "Sam W.": 10, "Maria S.": 5 },
    { facility: "Lancelot Farm LLC", "Lisa P.": 15, "Tom B.": 0 },
    { facility: "Tusk Farm LLC", "Carlos M.": 7, "Anna K.": 6 },
    { facility: "Razorback Farm LLC", "Dave L.": 11, "Sam W.": 0 },
    { facility: "Volunteer Farm LLC", "Jake R.": 8, "Lisa P.": 4 },
  ],
  "3Mo": [
    { facility: "NE Warehouse", "Jake R.": 36, "Maria S.": 0 },
    { facility: "Seahawk Farm LLC", "Maria S.": 52, "Tom B.": 0 },
    { facility: "Bugeater Farm LLC", "Tom B.": 40, "Carlos M.": 0 },
    { facility: "Y24 Farm LLC", "Carlos M.": 32, "Anna K.": 22 },
    { facility: "Bobcat Farm LLC", "Jake R.": 44, "Dave L.": 0 },
    { facility: "Wolfpack Farm LLC", "Anna K.": 26, "Sam W.": 0 },
    { facility: "Tar Heel Farm LLC", "Dave L.": 38, "Lisa P.": 0 },
    { facility: "Wildcat Farm LLC", "Sam W.": 28, "Maria S.": 14 },
    { facility: "Lancelot Farm LLC", "Lisa P.": 42, "Tom B.": 0 },
    { facility: "Tusk Farm LLC", "Carlos M.": 20, "Anna K.": 17 },
    { facility: "Razorback Farm LLC", "Dave L.": 30, "Sam W.": 0 },
    { facility: "Volunteer Farm LLC", "Jake R.": 22, "Lisa P.": 12 },
  ],
  "6Mo": [
    { facility: "NE Warehouse", "Jake R.": 68, "Maria S.": 0 },
    { facility: "Seahawk Farm LLC", "Maria S.": 98, "Tom B.": 0 },
    { facility: "Bugeater Farm LLC", "Tom B.": 76, "Carlos M.": 0 },
    { facility: "Y24 Farm LLC", "Carlos M.": 60, "Anna K.": 42 },
    { facility: "Bobcat Farm LLC", "Jake R.": 84, "Dave L.": 0 },
    { facility: "Wolfpack Farm LLC", "Anna K.": 48, "Sam W.": 0 },
    { facility: "Tar Heel Farm LLC", "Dave L.": 72, "Lisa P.": 0 },
    { facility: "Wildcat Farm LLC", "Sam W.": 52, "Maria S.": 26 },
    { facility: "Lancelot Farm LLC", "Lisa P.": 80, "Tom B.": 0 },
    { facility: "Tusk Farm LLC", "Carlos M.": 38, "Anna K.": 32 },
    { facility: "Razorback Farm LLC", "Dave L.": 56, "Sam W.": 0 },
    { facility: "Volunteer Farm LLC", "Jake R.": 42, "Lisa P.": 22 },
  ],
  "12Mo": [
    { facility: "NE Warehouse", "Jake R.": 130, "Maria S.": 0 },
    { facility: "Seahawk Farm LLC", "Maria S.": 188, "Tom B.": 0 },
    { facility: "Bugeater Farm LLC", "Tom B.": 145, "Carlos M.": 0 },
    { facility: "Y24 Farm LLC", "Carlos M.": 115, "Anna K.": 80 },
    { facility: "Bobcat Farm LLC", "Jake R.": 160, "Dave L.": 0 },
    { facility: "Wolfpack Farm LLC", "Anna K.": 92, "Sam W.": 0 },
    { facility: "Tar Heel Farm LLC", "Dave L.": 138, "Lisa P.": 0 },
    { facility: "Wildcat Farm LLC", "Sam W.": 100, "Maria S.": 50 },
    { facility: "Lancelot Farm LLC", "Lisa P.": 152, "Tom B.": 0 },
    { facility: "Tusk Farm LLC", "Carlos M.": 72, "Anna K.": 62 },
    { facility: "Razorback Farm LLC", "Dave L.": 108, "Sam W.": 0 },
    { facility: "Volunteer Farm LLC", "Jake R.": 80, "Lisa P.": 42 },
  ],
};

function getActiveGrowers(data: FacilityRow[]) {
  return Array.from(
    new Set(data.flatMap((d) => Object.keys(d).filter((k) => k !== "facility" && (d[k] as number) > 0)))
  );
}

const gallusUsers = ["Mike T.", "Sarah L.", "Ben K.", "Rachel W.", "Omar H.", "Jenny C.", "Paul D.", "Nina G."];
const gallusUserColors: Record<string, string> = {
  "Mike T.": "#2d7a4f",
  "Sarah L.": "#3b82c4",
  "Ben K.": "#e0a040",
  "Rachel W.": "#5ba8a0",
  "Omar H.": "#e88c6a",
  "Jenny C.": "#7b6fb0",
  "Paul D.": "#c46b8a",
  "Nina G.": "#4a9065",
};
const gallusTaskDataByPeriod: Record<GlobalTimePeriod, RoleRow[]> = {
  "1W": [
    { role: "R&M Team", "Mike T.": 3, "Sarah L.": 2, "Ben K.": 2, "Rachel W.": 1 },
    { role: "R&M Mgmt", "Omar H.": 2, "Jenny C.": 1, "Paul D.": 1 },
    { role: "Live Prod Mgmt", "Nina G.": 2, "Rachel W.": 1, "Jenny C.": 0 },
  ],
  "3W": [
    { role: "R&M Team", "Mike T.": 14, "Sarah L.": 12, "Ben K.": 9, "Rachel W.": 6 },
    { role: "R&M Mgmt", "Omar H.": 11, "Jenny C.": 8, "Paul D.": 3 },
    { role: "Live Prod Mgmt", "Nina G.": 10, "Rachel W.": 4, "Jenny C.": 3 },
  ],
  "30D": [
    { role: "R&M Team", "Mike T.": 18, "Sarah L.": 15, "Ben K.": 12, "Rachel W.": 8 },
    { role: "R&M Mgmt", "Omar H.": 14, "Jenny C.": 10, "Paul D.": 4 },
    { role: "Live Prod Mgmt", "Nina G.": 13, "Rachel W.": 5, "Jenny C.": 4 },
  ],
  "3Mo": [
    { role: "R&M Team", "Mike T.": 52, "Sarah L.": 44, "Ben K.": 34, "Rachel W.": 22 },
    { role: "R&M Mgmt", "Omar H.": 40, "Jenny C.": 28, "Paul D.": 12 },
    { role: "Live Prod Mgmt", "Nina G.": 36, "Rachel W.": 14, "Jenny C.": 10 },
  ],
  "6Mo": [
    { role: "R&M Team", "Mike T.": 98, "Sarah L.": 82, "Ben K.": 64, "Rachel W.": 40 },
    { role: "R&M Mgmt", "Omar H.": 74, "Jenny C.": 52, "Paul D.": 22 },
    { role: "Live Prod Mgmt", "Nina G.": 68, "Rachel W.": 26, "Jenny C.": 18 },
  ],
  "12Mo": [
    { role: "R&M Team", "Mike T.": 188, "Sarah L.": 156, "Ben K.": 120, "Rachel W.": 76 },
    { role: "R&M Mgmt", "Omar H.": 140, "Jenny C.": 98, "Paul D.": 42 },
    { role: "Live Prod Mgmt", "Nina G.": 128, "Rachel W.": 48, "Jenny C.": 34 },
  ],
};

function getActiveGallusUsers(data: RoleRow[]) {
  return Array.from(
    new Set(data.flatMap((d) => Object.keys(d).filter((k) => k !== "role" && (d[k] as number) > 0)))
  );
}

const users = ["Jake R.", "Maria S.", "Tom B.", "Carlos M.", "Anna K.", "Dave L.", "Mike T.", "Sarah L.", "Ben K.", "Rachel W.", "Omar H.", "Jenny C."];

const userActivityData: Record<GlobalTimePeriod, ActivityRow[]> = {
  "1W": users.map((u, i) => ({
    user: u,
    Hours: [4, 6, 3, 7, 2, 5, 4, 3, 6, 2, 5, 3][i],
    Activities: [3, 5, 4, 6, 2, 4, 5, 2, 4, 3, 6, 2][i],
    Observations: [2, 4, 2, 5, 1, 3, 3, 1, 4, 2, 3, 1][i],
    "Asset Moves": [1, 2, 1, 3, 0, 2, 2, 1, 1, 0, 2, 1][i],
  })),
  "3W": users.map((u, i) => ({
    user: u,
    Hours: [12, 18, 10, 21, 7, 15, 12, 9, 17, 6, 14, 9][i],
    Activities: [9, 14, 11, 18, 6, 12, 14, 7, 11, 8, 16, 6][i],
    Observations: [6, 11, 5, 14, 3, 9, 8, 4, 10, 5, 9, 3][i],
    "Asset Moves": [3, 6, 3, 9, 1, 5, 4, 2, 3, 1, 6, 2][i],
  })),
  "30D": users.map((u, i) => ({
    user: u,
    Hours: [18, 24, 14, 28, 11, 20, 16, 13, 22, 9, 19, 12][i],
    Activities: [14, 20, 16, 24, 9, 17, 18, 10, 15, 11, 22, 8][i],
    Observations: [9, 15, 8, 19, 5, 12, 11, 6, 14, 7, 13, 5][i],
    "Asset Moves": [5, 8, 4, 11, 2, 7, 6, 3, 5, 2, 8, 3][i],
  })),
  "3Mo": users.map((u, i) => ({
    user: u,
    Hours: [72, 96, 58, 110, 44, 81, 64, 52, 88, 38, 76, 48][i],
    Activities: [55, 78, 62, 94, 36, 68, 70, 40, 60, 44, 86, 32][i],
    Observations: [36, 58, 32, 74, 20, 47, 42, 24, 54, 28, 50, 20][i],
    "Asset Moves": [19, 31, 16, 43, 8, 27, 22, 12, 20, 8, 30, 12][i],
  })),
  "6Mo": users.map((u, i) => ({
    user: u,
    Hours: [140, 185, 110, 215, 85, 155, 125, 100, 170, 75, 145, 93][i],
    Activities: [105, 150, 120, 180, 70, 130, 135, 78, 115, 85, 165, 63][i],
    Observations: [70, 110, 63, 143, 39, 91, 80, 48, 105, 55, 98, 40][i],
    "Asset Moves": [37, 60, 31, 84, 16, 52, 43, 24, 39, 16, 58, 23][i],
  })),
  "12Mo": users.map((u, i) => ({
    user: u,
    Hours: [280, 370, 220, 430, 170, 310, 250, 200, 340, 150, 290, 185][i],
    Activities: [210, 300, 240, 360, 140, 260, 270, 155, 230, 170, 330, 125][i],
    Observations: [140, 220, 125, 285, 78, 182, 160, 95, 210, 110, 195, 80][i],
    "Asset Moves": [74, 120, 62, 168, 31, 104, 86, 48, 78, 32, 116, 46][i],
  })),
};

const activityMetrics: { key: ActivityMetricKey; color: string }[] = [
  { key: "Hours", color: "#2d7a4f" },
  { key: "Observations", color: "#e0a040" },
  { key: "Asset Moves", color: "#5ba8a0" },
  { key: "WRs Created", color: "#7c3aed" },
  { key: "Tasks Completed", color: "#0891b2" },
  { key: "Activities Logged", color: "#be185d" },
];

const stockAdjustmentsDataByPeriod: Record<GlobalTimePeriod, { week: string; value: number; dollars: number }[]> = {
  "1W": [
    { week: "Mon", value: 2, dollars: 620 },
    { week: "Tue", value: 1, dollars: 310 },
    { week: "Wed", value: 3, dollars: 940 },
    { week: "Thu", value: 1, dollars: 280 },
    { week: "Fri", value: 2, dollars: 580 },
    { week: "Sat", value: 1, dollars: 190 },
    { week: "Sun", value: 0, dollars: 0 },
  ],
  "3W": [
    { week: "Week 1", value: 11, dollars: 3820 },
    { week: "Week 2", value: 7, dollars: 1960 },
    { week: "Week 3", value: 9, dollars: 2890 },
  ],
  "30D": [
    { week: "Wk 1", value: 8, dollars: 2450 },
    { week: "Wk 2", value: 5, dollars: 1280 },
    { week: "Wk 3", value: 11, dollars: 3820 },
    { week: "Wk 4", value: 7, dollars: 1960 },
  ],
  "3Mo": [
    { week: "Oct", value: 28, dollars: 8640 },
    { week: "Nov", value: 32, dollars: 10240 },
    { week: "Dec", value: 26, dollars: 7890 },
  ],
  "6Mo": [
    { week: "Jul", value: 22, dollars: 6520 },
    { week: "Aug", value: 25, dollars: 7380 },
    { week: "Sep", value: 30, dollars: 9120 },
    { week: "Oct", value: 28, dollars: 8640 },
    { week: "Nov", value: 32, dollars: 10240 },
    { week: "Dec", value: 26, dollars: 7890 },
  ],
  "12Mo": [
    { week: "Jan", value: 18, dollars: 5200 },
    { week: "Feb", value: 20, dollars: 5840 },
    { week: "Mar", value: 22, dollars: 6380 },
    { week: "Apr", value: 19, dollars: 5620 },
    { week: "May", value: 24, dollars: 7100 },
    { week: "Jun", value: 21, dollars: 6280 },
    { week: "Jul", value: 22, dollars: 6520 },
    { week: "Aug", value: 25, dollars: 7380 },
    { week: "Sep", value: 30, dollars: 9120 },
    { week: "Oct", value: 28, dollars: 8640 },
    { week: "Nov", value: 32, dollars: 10240 },
    { week: "Dec", value: 26, dollars: 7890 },
  ],
};

const stockPickingDataByPeriod: Record<GlobalTimePeriod, StockPickRow[]> = {
  "1W": [
    { week: "Mon", Picking: 3, pickingDollars: 920, pickingUsers: [{ user: "Jake R.", count: 2 }, { user: "Maria S.", count: 1 }], Returned: 1, returnedDollars: 240, returnedUsers: [{ user: "Anna K.", count: 1 }], Used: 2, usedDollars: 680, usedUsers: [{ user: "Jake R.", count: 1 }, { user: "Carlos M.", count: 1 }] },
    { week: "Tue", Picking: 4, pickingDollars: 1280, pickingUsers: [{ user: "Tom B.", count: 2 }, { user: "Dave L.", count: 2 }], Returned: 0, returnedDollars: 0, returnedUsers: [], Used: 3, usedDollars: 940, usedUsers: [{ user: "Tom B.", count: 2 }, { user: "Maria S.", count: 1 }] },
    { week: "Wed", Picking: 5, pickingDollars: 1640, pickingUsers: [{ user: "Carlos M.", count: 3 }, { user: "Jake R.", count: 2 }], Returned: 2, returnedDollars: 480, returnedUsers: [{ user: "Anna K.", count: 1 }, { user: "Jake R.", count: 1 }], Used: 3, usedDollars: 1020, usedUsers: [{ user: "Carlos M.", count: 2 }, { user: "Jake R.", count: 1 }] },
    { week: "Thu", Picking: 2, pickingDollars: 640, pickingUsers: [{ user: "Maria S.", count: 1 }, { user: "Dave L.", count: 1 }], Returned: 1, returnedDollars: 190, returnedUsers: [{ user: "Tom B.", count: 1 }], Used: 2, usedDollars: 580, usedUsers: [{ user: "Maria S.", count: 1 }, { user: "Dave L.", count: 1 }] },
    { week: "Fri", Picking: 3, pickingDollars: 980, pickingUsers: [{ user: "Jake R.", count: 1 }, { user: "Anna K.", count: 1 }, { user: "Tom B.", count: 1 }], Returned: 1, returnedDollars: 310, returnedUsers: [{ user: "Anna K.", count: 1 }], Used: 2, usedDollars: 620, usedUsers: [{ user: "Jake R.", count: 1 }, { user: "Carlos M.", count: 1 }] },
  ],
  "3W": [
    { week: "Week 1", Picking: 18, pickingDollars: 5910, pickingUsers: [{ user: "Carlos M.", count: 6 }, { user: "Jake R.", count: 5 }, { user: "Maria S.", count: 4 }, { user: "Dave L.", count: 3 }], Returned: 5, returnedDollars: 1340, returnedUsers: [{ user: "Anna K.", count: 3 }, { user: "Jake R.", count: 2 }], Used: 13, usedDollars: 4420, usedUsers: [{ user: "Carlos M.", count: 5 }, { user: "Jake R.", count: 4 }, { user: "Maria S.", count: 4 }] },
    { week: "Week 2", Picking: 16, pickingDollars: 5140, pickingUsers: [{ user: "Jake R.", count: 5 }, { user: "Carlos M.", count: 4 }, { user: "Anna K.", count: 4 }, { user: "Tom B.", count: 3 }], Returned: 6, returnedDollars: 1680, returnedUsers: [{ user: "Tom B.", count: 3 }, { user: "Anna K.", count: 3 }], Used: 10, usedDollars: 3340, usedUsers: [{ user: "Jake R.", count: 4 }, { user: "Carlos M.", count: 3 }, { user: "Anna K.", count: 3 }] },
    { week: "Week 3", Picking: 12, pickingDollars: 3960, pickingUsers: [{ user: "Dave L.", count: 4 }, { user: "Maria S.", count: 4 }, { user: "Jake R.", count: 4 }], Returned: 3, returnedDollars: 890, returnedUsers: [{ user: "Maria S.", count: 2 }, { user: "Carlos M.", count: 1 }], Used: 9, usedDollars: 2950, usedUsers: [{ user: "Dave L.", count: 3 }, { user: "Maria S.", count: 3 }, { user: "Jake R.", count: 3 }] },
  ],
  "30D": [
    { week: "Wk 1", Picking: 14, pickingDollars: 4250, pickingUsers: [{ user: "Jake R.", count: 5 }, { user: "Maria S.", count: 4 }, { user: "Tom B.", count: 3 }, { user: "Carlos M.", count: 2 }], Returned: 4, returnedDollars: 980, returnedUsers: [{ user: "Anna K.", count: 2 }, { user: "Dave L.", count: 2 }], Used: 10, usedDollars: 3120, usedUsers: [{ user: "Jake R.", count: 4 }, { user: "Maria S.", count: 3 }, { user: "Carlos M.", count: 3 }] },
    { week: "Wk 2", Picking: 11, pickingDollars: 3680, pickingUsers: [{ user: "Tom B.", count: 4 }, { user: "Jake R.", count: 3 }, { user: "Anna K.", count: 2 }, { user: "Dave L.", count: 2 }], Returned: 3, returnedDollars: 720, returnedUsers: [{ user: "Maria S.", count: 2 }, { user: "Tom B.", count: 1 }], Used: 8, usedDollars: 2840, usedUsers: [{ user: "Tom B.", count: 3 }, { user: "Jake R.", count: 3 }, { user: "Anna K.", count: 2 }] },
    { week: "Wk 3", Picking: 18, pickingDollars: 5910, pickingUsers: [{ user: "Carlos M.", count: 6 }, { user: "Jake R.", count: 5 }, { user: "Maria S.", count: 4 }, { user: "Dave L.", count: 3 }], Returned: 5, returnedDollars: 1340, returnedUsers: [{ user: "Anna K.", count: 3 }, { user: "Jake R.", count: 2 }], Used: 13, usedDollars: 4420, usedUsers: [{ user: "Carlos M.", count: 5 }, { user: "Jake R.", count: 4 }, { user: "Maria S.", count: 4 }] },
    { week: "Wk 4", Picking: 9, pickingDollars: 2870, pickingUsers: [{ user: "Maria S.", count: 3 }, { user: "Dave L.", count: 3 }, { user: "Tom B.", count: 3 }], Returned: 2, returnedDollars: 560, returnedUsers: [{ user: "Carlos M.", count: 1 }, { user: "Dave L.", count: 1 }], Used: 7, usedDollars: 2190, usedUsers: [{ user: "Maria S.", count: 3 }, { user: "Dave L.", count: 2 }, { user: "Tom B.", count: 2 }] },
  ],
  "3Mo": [
    { week: "Oct", Picking: 52, pickingDollars: 16710, pickingUsers: [{ user: "Jake R.", count: 18 }, { user: "Carlos M.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Tom B.", count: 8 }], Returned: 14, returnedDollars: 3620, returnedUsers: [{ user: "Anna K.", count: 8 }, { user: "Tom B.", count: 6 }], Used: 38, usedDollars: 12420, usedUsers: [{ user: "Carlos M.", count: 14 }, { user: "Jake R.", count: 12 }, { user: "Maria S.", count: 12 }] },
    { week: "Nov", Picking: 58, pickingDollars: 18640, pickingUsers: [{ user: "Carlos M.", count: 20 }, { user: "Jake R.", count: 16 }, { user: "Dave L.", count: 12 }, { user: "Maria S.", count: 10 }], Returned: 16, returnedDollars: 4280, returnedUsers: [{ user: "Anna K.", count: 9 }, { user: "Jake R.", count: 7 }], Used: 42, usedDollars: 14080, usedUsers: [{ user: "Jake R.", count: 16 }, { user: "Carlos M.", count: 14 }, { user: "Dave L.", count: 12 }] },
    { week: "Dec", Picking: 48, pickingDollars: 15480, pickingUsers: [{ user: "Dave L.", count: 16 }, { user: "Maria S.", count: 14 }, { user: "Jake R.", count: 10 }, { user: "Anna K.", count: 8 }], Returned: 12, returnedDollars: 3240, returnedUsers: [{ user: "Maria S.", count: 7 }, { user: "Carlos M.", count: 5 }], Used: 36, usedDollars: 11640, usedUsers: [{ user: "Dave L.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Jake R.", count: 10 }] },
  ],
  "6Mo": [
    { week: "Jul", Picking: 42, pickingDollars: 13520, pickingUsers: [{ user: "Jake R.", count: 14 }, { user: "Tom B.", count: 12 }, { user: "Maria S.", count: 10 }, { user: "Carlos M.", count: 6 }], Returned: 10, returnedDollars: 2680, returnedUsers: [{ user: "Anna K.", count: 6 }, { user: "Dave L.", count: 4 }], Used: 32, usedDollars: 10280, usedUsers: [{ user: "Jake R.", count: 12 }, { user: "Tom B.", count: 10 }, { user: "Maria S.", count: 10 }] },
    { week: "Aug", Picking: 46, pickingDollars: 14840, pickingUsers: [{ user: "Carlos M.", count: 16 }, { user: "Jake R.", count: 14 }, { user: "Maria S.", count: 10 }, { user: "Dave L.", count: 6 }], Returned: 12, returnedDollars: 3180, returnedUsers: [{ user: "Anna K.", count: 7 }, { user: "Tom B.", count: 5 }], Used: 34, usedDollars: 11040, usedUsers: [{ user: "Carlos M.", count: 12 }, { user: "Jake R.", count: 12 }, { user: "Maria S.", count: 10 }] },
    { week: "Sep", Picking: 50, pickingDollars: 16120, pickingUsers: [{ user: "Jake R.", count: 16 }, { user: "Carlos M.", count: 14 }, { user: "Tom B.", count: 12 }, { user: "Anna K.", count: 8 }], Returned: 14, returnedDollars: 3720, returnedUsers: [{ user: "Tom B.", count: 8 }, { user: "Anna K.", count: 6 }], Used: 36, usedDollars: 11820, usedUsers: [{ user: "Jake R.", count: 14 }, { user: "Carlos M.", count: 12 }, { user: "Tom B.", count: 10 }] },
    { week: "Oct", Picking: 52, pickingDollars: 16710, pickingUsers: [{ user: "Jake R.", count: 18 }, { user: "Carlos M.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Tom B.", count: 8 }], Returned: 14, returnedDollars: 3620, returnedUsers: [{ user: "Anna K.", count: 8 }, { user: "Tom B.", count: 6 }], Used: 38, usedDollars: 12420, usedUsers: [{ user: "Carlos M.", count: 14 }, { user: "Jake R.", count: 12 }, { user: "Maria S.", count: 12 }] },
    { week: "Nov", Picking: 58, pickingDollars: 18640, pickingUsers: [{ user: "Carlos M.", count: 20 }, { user: "Jake R.", count: 16 }, { user: "Dave L.", count: 12 }, { user: "Maria S.", count: 10 }], Returned: 16, returnedDollars: 4280, returnedUsers: [{ user: "Anna K.", count: 9 }, { user: "Jake R.", count: 7 }], Used: 42, usedDollars: 14080, usedUsers: [{ user: "Jake R.", count: 16 }, { user: "Carlos M.", count: 14 }, { user: "Dave L.", count: 12 }] },
    { week: "Dec", Picking: 48, pickingDollars: 15480, pickingUsers: [{ user: "Dave L.", count: 16 }, { user: "Maria S.", count: 14 }, { user: "Jake R.", count: 10 }, { user: "Anna K.", count: 8 }], Returned: 12, returnedDollars: 3240, returnedUsers: [{ user: "Maria S.", count: 7 }, { user: "Carlos M.", count: 5 }], Used: 36, usedDollars: 11640, usedUsers: [{ user: "Dave L.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Jake R.", count: 10 }] },
  ],
  "12Mo": [
    { week: "Jan", Picking: 35, pickingDollars: 11280, pickingUsers: [{ user: "Jake R.", count: 12 }, { user: "Tom B.", count: 10 }, { user: "Maria S.", count: 8 }, { user: "Carlos M.", count: 5 }], Returned: 8, returnedDollars: 2140, returnedUsers: [{ user: "Anna K.", count: 5 }, { user: "Dave L.", count: 3 }], Used: 27, usedDollars: 8640, usedUsers: [{ user: "Jake R.", count: 10 }, { user: "Tom B.", count: 9 }, { user: "Maria S.", count: 8 }] },
    { week: "Feb", Picking: 38, pickingDollars: 12240, pickingUsers: [{ user: "Carlos M.", count: 12 }, { user: "Jake R.", count: 10 }, { user: "Maria S.", count: 10 }, { user: "Dave L.", count: 6 }], Returned: 9, returnedDollars: 2380, returnedUsers: [{ user: "Anna K.", count: 5 }, { user: "Tom B.", count: 4 }], Used: 29, usedDollars: 9360, usedUsers: [{ user: "Carlos M.", count: 10 }, { user: "Jake R.", count: 10 }, { user: "Maria S.", count: 9 }] },
    { week: "Mar", Picking: 40, pickingDollars: 12880, pickingUsers: [{ user: "Jake R.", count: 14 }, { user: "Tom B.", count: 10 }, { user: "Carlos M.", count: 10 }, { user: "Anna K.", count: 6 }], Returned: 10, returnedDollars: 2680, returnedUsers: [{ user: "Tom B.", count: 6 }, { user: "Anna K.", count: 4 }], Used: 30, usedDollars: 9840, usedUsers: [{ user: "Jake R.", count: 12 }, { user: "Tom B.", count: 10 }, { user: "Carlos M.", count: 8 }] },
    { week: "Apr", Picking: 37, pickingDollars: 11920, pickingUsers: [{ user: "Maria S.", count: 12 }, { user: "Dave L.", count: 10 }, { user: "Jake R.", count: 10 }, { user: "Tom B.", count: 5 }], Returned: 8, returnedDollars: 2100, returnedUsers: [{ user: "Carlos M.", count: 4 }, { user: "Dave L.", count: 4 }], Used: 28, usedDollars: 9040, usedUsers: [{ user: "Maria S.", count: 10 }, { user: "Dave L.", count: 10 }, { user: "Jake R.", count: 8 }] },
    { week: "May", Picking: 44, pickingDollars: 14160, pickingUsers: [{ user: "Jake R.", count: 14 }, { user: "Carlos M.", count: 12 }, { user: "Anna K.", count: 10 }, { user: "Maria S.", count: 8 }], Returned: 12, returnedDollars: 3200, returnedUsers: [{ user: "Anna K.", count: 7 }, { user: "Jake R.", count: 5 }], Used: 32, usedDollars: 10480, usedUsers: [{ user: "Jake R.", count: 12 }, { user: "Carlos M.", count: 10 }, { user: "Anna K.", count: 10 }] },
    { week: "Jun", Picking: 41, pickingDollars: 13200, pickingUsers: [{ user: "Dave L.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Jake R.", count: 8 }, { user: "Tom B.", count: 7 }], Returned: 10, returnedDollars: 2720, returnedUsers: [{ user: "Maria S.", count: 6 }, { user: "Carlos M.", count: 4 }], Used: 31, usedDollars: 10080, usedUsers: [{ user: "Dave L.", count: 12 }, { user: "Maria S.", count: 10 }, { user: "Jake R.", count: 9 }] },
    { week: "Jul", Picking: 42, pickingDollars: 13520, pickingUsers: [{ user: "Jake R.", count: 14 }, { user: "Tom B.", count: 12 }, { user: "Maria S.", count: 10 }, { user: "Carlos M.", count: 6 }], Returned: 10, returnedDollars: 2680, returnedUsers: [{ user: "Anna K.", count: 6 }, { user: "Dave L.", count: 4 }], Used: 32, usedDollars: 10280, usedUsers: [{ user: "Jake R.", count: 12 }, { user: "Tom B.", count: 10 }, { user: "Maria S.", count: 10 }] },
    { week: "Aug", Picking: 46, pickingDollars: 14840, pickingUsers: [{ user: "Carlos M.", count: 16 }, { user: "Jake R.", count: 14 }, { user: "Maria S.", count: 10 }, { user: "Dave L.", count: 6 }], Returned: 12, returnedDollars: 3180, returnedUsers: [{ user: "Anna K.", count: 7 }, { user: "Tom B.", count: 5 }], Used: 34, usedDollars: 11040, usedUsers: [{ user: "Carlos M.", count: 12 }, { user: "Jake R.", count: 12 }, { user: "Maria S.", count: 10 }] },
    { week: "Sep", Picking: 50, pickingDollars: 16120, pickingUsers: [{ user: "Jake R.", count: 16 }, { user: "Carlos M.", count: 14 }, { user: "Tom B.", count: 12 }, { user: "Anna K.", count: 8 }], Returned: 14, returnedDollars: 3720, returnedUsers: [{ user: "Tom B.", count: 8 }, { user: "Anna K.", count: 6 }], Used: 36, usedDollars: 11820, usedUsers: [{ user: "Jake R.", count: 14 }, { user: "Carlos M.", count: 12 }, { user: "Tom B.", count: 10 }] },
    { week: "Oct", Picking: 52, pickingDollars: 16710, pickingUsers: [{ user: "Jake R.", count: 18 }, { user: "Carlos M.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Tom B.", count: 8 }], Returned: 14, returnedDollars: 3620, returnedUsers: [{ user: "Anna K.", count: 8 }, { user: "Tom B.", count: 6 }], Used: 38, usedDollars: 12420, usedUsers: [{ user: "Carlos M.", count: 14 }, { user: "Jake R.", count: 12 }, { user: "Maria S.", count: 12 }] },
    { week: "Nov", Picking: 58, pickingDollars: 18640, pickingUsers: [{ user: "Carlos M.", count: 20 }, { user: "Jake R.", count: 16 }, { user: "Dave L.", count: 12 }, { user: "Maria S.", count: 10 }], Returned: 16, returnedDollars: 4280, returnedUsers: [{ user: "Anna K.", count: 9 }, { user: "Jake R.", count: 7 }], Used: 42, usedDollars: 14080, usedUsers: [{ user: "Jake R.", count: 16 }, { user: "Carlos M.", count: 14 }, { user: "Dave L.", count: 12 }] },
    { week: "Dec", Picking: 48, pickingDollars: 15480, pickingUsers: [{ user: "Dave L.", count: 16 }, { user: "Maria S.", count: 14 }, { user: "Jake R.", count: 10 }, { user: "Anna K.", count: 8 }], Returned: 12, returnedDollars: 3240, returnedUsers: [{ user: "Maria S.", count: 7 }, { user: "Carlos M.", count: 5 }], Used: 36, usedDollars: 11640, usedUsers: [{ user: "Dave L.", count: 14 }, { user: "Maria S.", count: 12 }, { user: "Jake R.", count: 10 }] },
  ],
};

const stockPickingColors = { Picking: "#2d7a4f", Returned: "#e0a040", Used: "#3b82c4" };

const stockPickTypeConfig: { type: StockPickType; usersKey: keyof StockPickRow; dollarsKey: keyof StockPickRow }[] = [
  { type: "Picking", usersKey: "pickingUsers", dollarsKey: "pickingDollars" },
  { type: "Returned", usersKey: "returnedUsers", dollarsKey: "returnedDollars" },
  { type: "Used", usersKey: "usedUsers", dollarsKey: "usedDollars" },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const StockPickingTooltip = ({ active, payload, label }: { active?: boolean; payload?: { payload: StockPickRow }[]; label?: string }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div className="bg-white rounded-md border border-[#e5e7eb] p-2.5 shadow-sm text-[11px]">
      <p className="font-semibold text-[#111827] mb-1.5">{label?.split("|")[0]}</p>
      {stockPickTypeConfig.map(({ type, usersKey, dollarsKey }) => {
        const usersList = data[usersKey] as StockPickUser[];
        const dollars = data[dollarsKey] as number;
        if (!usersList || !data[type]) return null;
        return (
          <div key={type} className="mb-1.5">
            <p className="font-medium" style={{ color: stockPickingColors[type] }}>
              {type}: {data[type]} (${dollars.toLocaleString()})
            </p>
            {usersList.map((u) => (
              <p key={u.user} className="text-[#6b7280] pl-2">{u.user}: {u.count}</p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

function ChartCard({
  title,
  subtitle,
  children,
  rightHeader,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  rightHeader?: React.ReactNode;
}) {
  return (
    <div
      className="bg-white rounded-lg p-5 flex flex-col"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    >
      <div className="flex items-start justify-between gap-3 mb-1 shrink-0">
        <div>
          <h3 className="text-[14px] font-semibold text-[#111827]">{title}</h3>
          {subtitle && <p className="text-[12px] text-[#6b7280] mt-0.5">{subtitle}</p>}
        </div>
        {rightHeader}
      </div>
      <div className="mt-4 flex-1 min-h-0">{children}</div>
    </div>
  );
}

function GlobalTimeToggle({
  active,
  onChange,
}: {
  active: GlobalTimePeriod;
  onChange: (p: GlobalTimePeriod) => void;
}) {
  const periods: GlobalTimePeriod[] = ["1W", "3W", "30D", "3Mo", "6Mo", "12Mo"];
  return (
    <div className="flex gap-0.5">
      {periods.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${
            active === p
              ? "bg-[#2B5A2E] text-white"
              : "bg-white border border-gray-300 text-gray-500 hover:bg-gray-50"
          }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const periodToDays: Record<GlobalTimePeriod, number> = {
  "1W": 7, "3W": 21, "30D": 30, "3Mo": 90, "6Mo": 180, "12Mo": 365,
};

function ChartLoader({ height = 170 }: { height?: number }) {
  return (
    <div className="flex items-center justify-center" style={{ height }}>
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-[3px] border-[#e5e7eb]" />
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#2B5A2E] animate-spin" />
        </div>
        <span className="text-[11px] text-[#9ca3af] font-medium">Loading data...</span>
      </div>
    </div>
  );
}

interface AdoptionKpiData {
  totalWorkRequestsCreated: number;
  workRequestsChange: number;
  wrConvertedToTasksPercentage: number;
  conversionChange: number;
  avgTasksPerUser: number;
  activeUsers: number;
  avgHoursSpentPerTask: number;
  hoursChange: number;
  totalTasksWithChecklist: number;
  checklistChange: number;
  completedTasksWithChecklistPercentage: number;
}

const WR_ORIGIN_COLORS = ["#2d7a4f", "#3b82c4", "#e0a040", "#5ba8a0", "#e88c6a", "#7b6fb0", "#c46b8a", "#4a9065"];
const GALLUS_COLORS = ["#2d7a4f", "#3b82c4", "#e0a040", "#5ba8a0", "#e88c6a", "#7b6fb0", "#c46b8a", "#4a9065", "#d97706", "#6366f1", "#ec4899", "#14b8a6"];

const WR_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WR_DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function fmtWRDate(date: Date): string {
  return `${date.getDate()} ${WR_MONTHS[date.getMonth()]}`;
}

function buildWRDateLabels(count: number, days: number): string[] {
  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const labels: string[] = [];
  if (days <= 7) {
    for (let i = 0; i < count; i++) {
      const d = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      labels.push(`${WR_DAYS[d.getDay()]}|${fmtWRDate(d)}`);
    }
  } else if (days <= 31) {
    for (let i = 0; i < count; i++) {
      const wStart = new Date(startDate.getTime() + i * 7 * 24 * 60 * 60 * 1000);
      const wEnd = new Date(startDate.getTime() + Math.min((i + 1) * 7 - 1, days - 1) * 24 * 60 * 60 * 1000);
      const rangeStr = wStart.getMonth() === wEnd.getMonth()
        ? `${wStart.getDate()}-${wEnd.getDate()} ${WR_MONTHS[wEnd.getMonth()]}`
        : `${wStart.getDate()} ${WR_MONTHS[wStart.getMonth()]}-${wEnd.getDate()} ${WR_MONTHS[wEnd.getMonth()]}`;
      labels.push(`Week ${i + 1}|${rangeStr}`);
    }
  } else {
    for (let i = 0; i < count; i++) {
      const d = new Date(startDate.getTime() + i * 30 * 24 * 60 * 60 * 1000);
      labels.push(`${WR_MONTHS[d.getMonth()]}|${d.getFullYear()}`);
    }
  }
  return labels;
}

const WRXAxisTick = ({ x, y, payload }: any) => {
  const [line1, line2] = (payload.value as string).split("|");
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="middle" fill="#6b7280" fontSize={11}>{line1}</text>
      {line2 && <text x={0} y={0} dy={22} textAnchor="middle" fill="#9ca3af" fontSize={9}>{line2}</text>}
    </g>
  );
};

function transformWROrigins(rawData: any[], days: number): { chartData: Record<string, any>[]; userTypes: string[]; colorMap: Record<string, string> } {
  if (!rawData || !rawData.length) return { chartData: [], userTypes: [], colorMap: {} };
  const userTypes = Array.from(new Set(rawData.map((r: any) => r.userTypeName)));
  const colorMap: Record<string, string> = {};
  userTypes.forEach((ut, i) => { colorMap[ut] = WR_ORIGIN_COLORS[i % WR_ORIGIN_COLORS.length]; });

  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const buckets: Record<string, Record<string, number>> = {};
  const bucketOrder: string[] = [];

  if (days <= 7) {
    for (let i = 0; i < days; i++) {
      const dayDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      const label = `${WR_DAYS[dayDate.getDay()]}|${fmtWRDate(dayDate)}`;
      buckets[label] = {};
      bucketOrder.push(label);
    }
    rawData.forEach((r: any) => {
      const d = new Date(r.createdDate?.split("T")[0] || r.createdDate);
      const idx = Math.floor((d.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      if (idx >= 0 && idx < days) {
        const dayDate = new Date(startDate.getTime() + idx * 24 * 60 * 60 * 1000);
        const label = `${WR_DAYS[dayDate.getDay()]}|${fmtWRDate(dayDate)}`;
        buckets[label][r.userTypeName] = (buckets[label][r.userTypeName] || 0) + r.wrCount;
      }
    });
  } else if (days <= 31) {
    const numWeeks = Math.ceil(days / 7);
    for (let i = 0; i < numWeeks; i++) {
      const weekStart = new Date(startDate.getTime() + i * 7 * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(startDate.getTime() + Math.min((i + 1) * 7 - 1, days - 1) * 24 * 60 * 60 * 1000);
      const rangeStr = weekStart.getMonth() === weekEnd.getMonth()
        ? `${weekStart.getDate()}-${weekEnd.getDate()} ${WR_MONTHS[weekEnd.getMonth()]}`
        : `${weekStart.getDate()} ${WR_MONTHS[weekStart.getMonth()]}-${weekEnd.getDate()} ${WR_MONTHS[weekEnd.getMonth()]}`;
      const label = `Week ${i + 1}|${rangeStr}`;
      buckets[label] = {};
      bucketOrder.push(label);
    }
    rawData.forEach((r: any) => {
      const d = new Date(r.createdDate?.split("T")[0] || r.createdDate);
      const daysFrom = Math.floor((d.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      const weekIdx = Math.min(Math.floor(daysFrom / 7), numWeeks - 1);
      if (weekIdx >= 0) {
        const weekStart = new Date(startDate.getTime() + weekIdx * 7 * 24 * 60 * 60 * 1000);
        const weekEnd = new Date(startDate.getTime() + Math.min((weekIdx + 1) * 7 - 1, days - 1) * 24 * 60 * 60 * 1000);
        const rangeStr = weekStart.getMonth() === weekEnd.getMonth()
          ? `${weekStart.getDate()}-${weekEnd.getDate()} ${WR_MONTHS[weekEnd.getMonth()]}`
          : `${weekStart.getDate()} ${WR_MONTHS[weekStart.getMonth()]}-${weekEnd.getDate()} ${WR_MONTHS[weekEnd.getMonth()]}`;
        const label = `Week ${weekIdx + 1}|${rangeStr}`;
        buckets[label][r.userTypeName] = (buckets[label][r.userTypeName] || 0) + r.wrCount;
      }
    });
  } else {
    const numMonths = Math.round(days / 30);
    for (let i = 0; i < numMonths; i++) {
      const monthDate = new Date(startDate.getTime() + i * 30 * 24 * 60 * 60 * 1000);
      const label = `${WR_MONTHS[monthDate.getMonth()]}|${monthDate.getFullYear()}`;
      buckets[label] = {};
      bucketOrder.push(label);
    }
    rawData.forEach((r: any) => {
      const d = new Date(r.createdDate?.split("T")[0] || r.createdDate);
      const daysFrom = Math.floor((d.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      const monthIdx = Math.min(Math.floor(daysFrom / 30), numMonths - 1);
      if (monthIdx >= 0) {
        const monthDate = new Date(startDate.getTime() + monthIdx * 30 * 24 * 60 * 60 * 1000);
        const label = `${WR_MONTHS[monthDate.getMonth()]}|${monthDate.getFullYear()}`;
        buckets[label][r.userTypeName] = (buckets[label][r.userTypeName] || 0) + r.wrCount;
      }
    });
  }

  const chartData = bucketOrder.map((label) => ({ week: label, ...buckets[label] }));
  return { chartData, userTypes, colorMap };
}

function transformFacilityOrigins(rawData: any[]): { chartData: FacilityRow[]; users: string[]; colorMap: Record<string, string> } {
  if (!rawData || !rawData.length) return { chartData: [], users: [], colorMap: {} };
  const byFacility: Record<string, Record<string, number>> = {};
  rawData.forEach((r: any) => {
    const fname = r.facilityName?.trim();
    if (!byFacility[fname]) byFacility[fname] = {};
    byFacility[fname][r.userName?.trim()] = (byFacility[fname][r.userName?.trim()] || 0) + r.taskCount;
  });
  const allUsers = Array.from(new Set(rawData.map((r: any) => r.userName?.trim())));
  const colorMap: Record<string, string> = {};
  allUsers.forEach((u, i) => { colorMap[u] = GALLUS_COLORS[i % GALLUS_COLORS.length]; });
  const chartData: FacilityRow[] = Object.entries(byFacility).map(([facility, users]) => ({ facility, ...users }));
  return { chartData, users: allUsers, colorMap };
}

function transformGallusOrigins(rawData: any[]): { chartData: RoleRow[]; users: string[]; colorMap: Record<string, string> } {
  if (!rawData || !rawData.length) return { chartData: [], users: [], colorMap: {} };
  const byRole: Record<string, Record<string, number>> = {};
  rawData.forEach((r: any) => {
    if (!byRole[r.userTypeName]) byRole[r.userTypeName] = {};
    byRole[r.userTypeName][r.userName?.trim()] = (byRole[r.userTypeName][r.userName?.trim()] || 0) + r.taskCount;
  });
  const allUsers = Array.from(new Set(rawData.map((r: any) => r.userName?.trim())));
  const colorMap: Record<string, string> = {};
  allUsers.forEach((u, i) => { colorMap[u] = GALLUS_COLORS[i % GALLUS_COLORS.length]; });
  const chartData: RoleRow[] = Object.entries(byRole).map(([role, users]) => ({ role, ...users }));
  return { chartData, users: allUsers, colorMap };
}

const AdoptionDashboard: React.FC = () => {
  const [timePeriod, setTimePeriod] = useState<GlobalTimePeriod>("30D");
  const [kpiData, setKpiData] = useState<AdoptionKpiData | null>(null);
  const [kpiLoading, setKpiLoading] = useState<boolean>(false);
  const [wrOriginData, setWrOriginData] = useState<any[]>([]);
  const [wrOriginLoading, setWrOriginLoading] = useState<boolean>(false);
  const [gallusData, setGallusData] = useState<any[]>([]);
  const [gallusLoading, setGallusLoading] = useState<boolean>(false);
  const [facilityData, setFacilityData] = useState<any[]>([]);
  const [facilityLoading, setFacilityLoading] = useState<boolean>(false);
  const [wrStatusData, setWrStatusData] = useState<{ wrNew: number; wrConverted: number; wrDeleted: number } | null>(null);
  const [wrStatusLoading, setWrStatusLoading] = useState<boolean>(false);
  const [activityRawData, setActivityRawData] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState<boolean>(false);
  const [wrCompletenessRawData, setWrCompletenessRawData] = useState<any[]>([]);
  const [wrCompletenessLoading, setWrCompletenessLoading] = useState<boolean>(false);
  const [stockAdjRawData, setStockAdjRawData] = useState<any[]>([]);
  const [stockAdjLoading, setStockAdjLoading] = useState<boolean>(false);
  const [invMovementData, setInvMovementData] = useState<any>(null);
  const [invMovementLoading, setInvMovementLoading] = useState<boolean>(false);
  const [isExportingTotalWRsPDF, setIsExportingTotalWRsPDF] = useState(false);
  const [isExportingTotalWRsXLS, setIsExportingTotalWRsXLS] = useState(false);
  const [noRootCauseCount, setNoRootCauseCount] = useState<number | null>(null);
  const [noRootCauseLoading, setNoRootCauseLoading] = useState(true);
  const [isExportingNoRootCausePDF, setIsExportingNoRootCausePDF] = useState(false);
  const [isExportingNoRootCauseXLS, setIsExportingNoRootCauseXLS] = useState(false);
  const [isExportingChecklistTasksPDF, setIsExportingChecklistTasksPDF] = useState(false);
  const [isExportingChecklistTasksXLS, setIsExportingChecklistTasksXLS] = useState(false);
  const [isExportingChecklistCompletionPDF, setIsExportingChecklistCompletionPDF] = useState(false);
  const [isExportingChecklistCompletionXLS, setIsExportingChecklistCompletionXLS] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);
  const [isExportingGrowerPDF, setIsExportingGrowerPDF] = useState(false);
  const [isExportingGrowerXLS, setIsExportingGrowerXLS] = useState(false);
  const [isExportingActivityPDF, setIsExportingActivityPDF] = useState(false);
  const [isExportingActivityXLS, setIsExportingActivityXLS] = useState(false);
  const [isExportingWRCompletenessPDF, setIsExportingWRCompletenessPDF] = useState(false);
  const [isExportingWRCompletenessXLS, setIsExportingWRCompletenessXLS] = useState(false);
  const [isExportingStockPDF, setIsExportingStockPDF] = useState(false);
  const [isExportingStockXLS, setIsExportingStockXLS] = useState(false);
  const [isExportingInvPDF, setIsExportingInvPDF] = useState(false);
  const [isExportingInvXLS, setIsExportingInvXLS] = useState(false);
  const [checklistByCreatorData, setChecklistByCreatorData] = useState<any[]>([]);
  const [checklistByCreatorLoading, setChecklistByCreatorLoading] = useState<boolean>(false);
  const [isExportingChecklistCreatorPDF, setIsExportingChecklistCreatorPDF] = useState(false);
  const [isExportingChecklistCreatorXLS, setIsExportingChecklistCreatorXLS] = useState(false);
  const [isExportingGallusPDF, setIsExportingGallusPDF] = useState(false);
  const [isExportingGallusXLS, setIsExportingGallusXLS] = useState(false);
  const [isExportingWRStatusPDF, setIsExportingWRStatusPDF] = useState(false);
  const [isExportingWRStatusXLS, setIsExportingWRStatusXLS] = useState(false);
  const dashboardApi = useDashboardApi();

  useEffect(() => {
    setKpiLoading(true);
    const days = periodToDays[timePeriod];
    dashboardApi.getAdoptionDashboardCounts(
      { Days: days },
      (response: any) => {
        const data = response?.data || response;
        setKpiData(data);
        setKpiLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch adoption dashboard counts:", error);
        setKpiLoading(false);
      }
    );
    setNoRootCauseLoading(true);
    dashboardApi.getCompletedWOsWithoutRootCause(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setNoRootCauseCount(d?.count ?? d?.Count ?? null);
        setNoRootCauseLoading(false);
      },
      () => { setNoRootCauseLoading(false); }
    );
    setWrOriginLoading(true);
    setGallusLoading(true);
    setFacilityLoading(true);
    dashboardApi.getWorkRequestOrigins(
      { Days: days },
      (response: any) => { setWrOriginData(response?.data || []); setWrOriginLoading(false); },
      (error: any) => { console.error("Failed to fetch WR origins:", error); setWrOriginLoading(false); }
    );
    dashboardApi.getTaskOriginsGallus(
      { Days: days },
      (response: any) => { setGallusData(response?.data || []); setGallusLoading(false); },
      (error: any) => { console.error("Failed to fetch task origins:", error); setGallusLoading(false); }
    );
    dashboardApi.getGrowerTaskOrigins(
      { Days: days },
      (response: any) => { setFacilityData(response?.data || []); setFacilityLoading(false); },
      (error: any) => { console.error("Failed to fetch grower task origins:", error); setFacilityLoading(false); }
    );
    setWrStatusLoading(true);
    dashboardApi.getWRStatusPieChart(
      { days },
      (response: any) => { setWrStatusData(response?.data || null); setWrStatusLoading(false); },
      (error: any) => { console.error("Failed to fetch WR status pie chart:", error); setWrStatusLoading(false); }
    );
    setActivityLoading(true);
    dashboardApi.getUserActivitySummary(
      { Days: days },
      (response: any) => { setActivityRawData(response?.data || []); setActivityLoading(false); },
      (error: any) => { console.error("Failed to fetch user activity:", error); setActivityLoading(false); }
    );
    setWrCompletenessLoading(true);
    dashboardApi.getWRCompletenessByFacility(
      { Days: days },
      (response: any) => { setWrCompletenessRawData(response?.data || []); setWrCompletenessLoading(false); },
      (error: any) => { console.error("Failed to fetch WR completeness:", error); setWrCompletenessLoading(false); }
    );
    setStockAdjLoading(true);
    dashboardApi.getStockAdjustmentChart(
      { Days: days },
      (response: any) => { setStockAdjRawData(response?.data || []); setStockAdjLoading(false); },
      (error: any) => { console.error("Failed to fetch stock adjustments:", error); setStockAdjLoading(false); }
    );
    setInvMovementLoading(true);
    dashboardApi.getInventoryMovementChart(
      { Days: days },
      (response: any) => { setInvMovementData(response); setInvMovementLoading(false); },
      (error: any) => { console.error("Failed to fetch inventory movement:", error); setInvMovementLoading(false); }
    );
    setChecklistByCreatorLoading(true);
    dashboardApi.getChecklistByCreator(
      { Days: days },
      (response: any) => { const rows = response?.data || []; setChecklistByCreatorData(Array.isArray(rows) ? rows : []); setChecklistByCreatorLoading(false); },
      (error: any) => { console.error("Failed to fetch checklist by creator:", error); setChecklistByCreatorLoading(false); }
    );
  }, [timePeriod]);

  const handleExportTotalWRsPDF = () => {
    setIsExportingTotalWRsPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportKpiTotalWRsToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'kpi_total_work_requests'); setIsExportingTotalWRsPDF(false); }, () => setIsExportingTotalWRsPDF(false));
  };
  const handleExportTotalWRsXLS = () => {
    setIsExportingTotalWRsXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportKpiTotalWRsToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'kpi_total_work_requests'); setIsExportingTotalWRsXLS(false); }, () => setIsExportingTotalWRsXLS(false));
  };
  const handleExportNoRootCausePDF = () => {
    setIsExportingNoRootCausePDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportCompletedWOsWithoutRootCauseToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'completed_wos_no_root_cause'); setIsExportingNoRootCausePDF(false); }, () => setIsExportingNoRootCausePDF(false));
  };
  const handleExportNoRootCauseXLS = () => {
    setIsExportingNoRootCauseXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportCompletedWOsWithoutRootCauseToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'completed_wos_no_root_cause'); setIsExportingNoRootCauseXLS(false); }, () => setIsExportingNoRootCauseXLS(false));
  };
  const handleExportChecklistTasksPDF = () => {
    setIsExportingChecklistTasksPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportKpiChecklistTasksToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'kpi_checklist_tasks'); setIsExportingChecklistTasksPDF(false); }, () => setIsExportingChecklistTasksPDF(false));
  };
  const handleExportChecklistTasksXLS = () => {
    setIsExportingChecklistTasksXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportKpiChecklistTasksToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'kpi_checklist_tasks'); setIsExportingChecklistTasksXLS(false); }, () => setIsExportingChecklistTasksXLS(false));
  };
  const handleExportChecklistCompletionPDF = () => {
    setIsExportingChecklistCompletionPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportKpiChecklistCompletionToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'kpi_checklist_completion'); setIsExportingChecklistCompletionPDF(false); }, () => setIsExportingChecklistCompletionPDF(false));
  };
  const handleExportChecklistCompletionXLS = () => {
    setIsExportingChecklistCompletionXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportKpiChecklistCompletionToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'kpi_checklist_completion'); setIsExportingChecklistCompletionXLS(false); }, () => setIsExportingChecklistCompletionXLS(false));
  };

  const handleExportPDF = () => {
    setIsExportingPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportWorkRequestOriginsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'work_request_origins'); setIsExportingPDF(false); },
      () => { setIsExportingPDF(false); }
    );
  };

  const handleExportXLS = () => {
    setIsExportingXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportWorkRequestOriginsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'work_request_origins'); setIsExportingXLS(false); },
      () => { setIsExportingXLS(false); }
    );
  };

  const handleExportGrowerPDF = () => {
    setIsExportingGrowerPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportGrowerTaskOriginsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'grower_task_origins'); setIsExportingGrowerPDF(false); },
      () => { setIsExportingGrowerPDF(false); }
    );
  };

  const handleExportGrowerXLS = () => {
    setIsExportingGrowerXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportGrowerTaskOriginsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'grower_task_origins'); setIsExportingGrowerXLS(false); },
      () => { setIsExportingGrowerXLS(false); }
    );
  };

  const handleExportActivityPDF = () => {
    setIsExportingActivityPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportUserActivitySummaryToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'user_activity_summary'); setIsExportingActivityPDF(false); },
      () => { setIsExportingActivityPDF(false); }
    );
  };

  const handleExportActivityXLS = () => {
    setIsExportingActivityXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportUserActivitySummaryToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'user_activity_summary'); setIsExportingActivityXLS(false); },
      () => { setIsExportingActivityXLS(false); }
    );
  };

  const handleExportWRCompletenessPDF = () => {
    setIsExportingWRCompletenessPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportWRCompletenessByFacilityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'wr_completeness_by_facility'); setIsExportingWRCompletenessPDF(false); },
      () => { setIsExportingWRCompletenessPDF(false); }
    );
  };

  const handleExportWRCompletenessXLS = () => {
    setIsExportingWRCompletenessXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportWRCompletenessByFacilityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'wr_completeness_by_facility'); setIsExportingWRCompletenessXLS(false); },
      () => { setIsExportingWRCompletenessXLS(false); }
    );
  };

  const handleExportStockPDF = () => {
    setIsExportingStockPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportStockAdjustmentToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'stock_adjustments'); setIsExportingStockPDF(false); },
      () => { setIsExportingStockPDF(false); }
    );
  };

  const handleExportStockXLS = () => {
    setIsExportingStockXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportStockAdjustmentToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'stock_adjustments'); setIsExportingStockXLS(false); },
      () => { setIsExportingStockXLS(false); }
    );
  };

  const handleExportInvPDF = () => {
    setIsExportingInvPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportInventoryMovementToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'inventory_movement'); setIsExportingInvPDF(false); },
      () => { setIsExportingInvPDF(false); }
    );
  };

  const handleExportInvXLS = () => {
    setIsExportingInvXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportInventoryMovementToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'inventory_movement'); setIsExportingInvXLS(false); },
      () => { setIsExportingInvXLS(false); }
    );
  };

  const handleExportChecklistCreatorPDF = () => {
    setIsExportingChecklistCreatorPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportChecklistByCreatorToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'checklist_by_creator'); setIsExportingChecklistCreatorPDF(false); },
      () => { setIsExportingChecklistCreatorPDF(false); }
    );
  };

  const handleExportChecklistCreatorXLS = () => {
    setIsExportingChecklistCreatorXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportChecklistByCreatorToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'checklist_by_creator'); setIsExportingChecklistCreatorXLS(false); },
      () => { setIsExportingChecklistCreatorXLS(false); }
    );
  };

  const handleExportGallusPDF = () => {
    setIsExportingGallusPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportTaskOriginsGallusToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_origins_gallus'); setIsExportingGallusPDF(false); },
      () => { setIsExportingGallusPDF(false); }
    );
  };

  const handleExportGallusXLS = () => {
    setIsExportingGallusXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportTaskOriginsGallusToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_origins_gallus'); setIsExportingGallusXLS(false); },
      () => { setIsExportingGallusXLS(false); }
    );
  };

  const handleExportWRStatusPDF = () => {
    setIsExportingWRStatusPDF(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportWRStatusPieChartToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'wr_status_pie_chart'); setIsExportingWRStatusPDF(false); },
      () => { setIsExportingWRStatusPDF(false); }
    );
  };

  const handleExportWRStatusXLS = () => {
    setIsExportingWRStatusXLS(true);
    const days = periodToDays[timePeriod];
    dashboardApi.exportWRStatusPieChartToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'wr_status_pie_chart'); setIsExportingWRStatusXLS(false); },
      () => { setIsExportingWRStatusXLS(false); }
    );
  };

  const periodLabel = globalPeriodLabels[timePeriod];
  const days = periodToDays[timePeriod];
  const { chartData: workRequestData, userTypes: wrUserTypes, colorMap: wrColorMap } = transformWROrigins(wrOriginData, days);
  const { chartData: gallusTaskData, users: activeGallusUserNames, colorMap: gallusColorMap } = transformGallusOrigins(gallusData);
  const { chartData: growersTaskData, users: activeFacilityUsers, colorMap: facilityColorMap } = transformFacilityOrigins(facilityData);
  const activityData = activityRawData
    .map((r: any) => ({
      user: r.userName?.trim(),
      Hours: Math.round((r.hours || 0) * 10) / 10,
      Observations: r.observations || 0,
      "Asset Moves": r.assetMoves || 0,
      "WRs Created": r.wrsCreated || 0,
      "Tasks Completed": r.tasksCompleted || 0,
      "Activities Logged": r.activitiesLogged || 0,
    }))
    .filter((r) => activityMetrics.some((m) => (r[m.key] as number) > 0));
  const stockAdjDateLabels = buildWRDateLabels(stockAdjRawData.length, days);
  const stockAdjData = stockAdjRawData.map((r: any, i: number) => ({
    week: stockAdjDateLabels[i] ?? r.bucketLabel,
    positiveCount: r.adjCountPositive || 0,
    positiveDollars: Math.round(r.dollarsPositive || 0),
    negativeCount: r.adjCountNegative || 0,
    negativeDollars: Math.round(r.dollarsNegative || 0),
  }));
  const invMovementRaw = invMovementData?.data || [];
  const stockPickDateLabels = buildWRDateLabels(invMovementRaw.length, days);
  const stockPickData = invMovementRaw.map((r: any, i: number) => {
    // Build user arrays from userBreakdown for this bucket
    const ub = invMovementData?.userBreakdown || [];
    const bucketUsers = ub.filter((u: any) => u.bucketLabel === r.bucketLabel);
    const pickingUsers = bucketUsers.filter((u: any) => u.movementType === "Picking").map((u: any) => ({ user: u.userName?.trim(), count: u.userCount }));
    const returnedUsers = bucketUsers.filter((u: any) => u.movementType === "Returned").map((u: any) => ({ user: u.userName?.trim(), count: u.userCount }));
    const usedUsers = bucketUsers.filter((u: any) => u.movementType === "Used").map((u: any) => ({ user: u.userName?.trim(), count: u.userCount }));
    return {
      week: stockPickDateLabels[i] ?? r.bucketLabel,
      Picking: r.picking || 0,
      pickingDollars: Math.round(r.pickingDollars || 0),
      pickingUsers,
      Returned: r.returned || 0,
      returnedDollars: Math.round(r.returnedDollars || 0),
      returnedUsers,
      Used: r.used || 0,
      usedDollars: Math.round(r.usedDollars || 0),
      usedUsers,
    };
  });

  return (
    <Layout>
      <Header title="Adoption Dashboard">
        <Header.Right>
          <GlobalTimeToggle active={timePeriod} onChange={setTimePeriod} />
        </Header.Right>
      </Header>
      <div className="p-4">
        <div className="p-6 pb-10">
          <div className="mb-5">
            <span className="text-[13px] text-[#6b7280]">Showing data for: <span className="font-medium text-[#374151]">{periodLabel}</span></span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {/* KPI: Total Work Requests Created */}
            <div
              className="bg-white rounded-lg py-4 px-5 flex flex-col"
              style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
            >
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-medium text-[#6b7280]">Total Work Requests Created</p>
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportTotalWRsPDF} disabled={isExportingTotalWRsPDF}>{isExportingTotalWRsPDF ? "Exporting..." : "PDF"}</Menu>
                    <Menu onClick={handleExportTotalWRsXLS} disabled={isExportingTotalWRsXLS}>{isExportingTotalWRsXLS ? "Exporting..." : "XLS"}</Menu>
                  </Dropdown.Content>
                </Dropdown>
              </div>
              <div className="flex items-center justify-between mt-1 h-[56px]">
                <p className="text-[28px] font-bold text-[#111827] leading-tight">
                  {kpiLoading ? "..." : kpiData ? kpiData.totalWorkRequestsCreated.toLocaleString() : "—"}
                </p>
                <div className="w-[80px] h-[50px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={wrSparklineData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                      <defs>
                        <linearGradient id="wrGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#16a34a" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#16a34a" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke="#16a34a" strokeWidth={1.5} fill="url(#wrGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <p className={`text-[11px] mt-auto pt-1 ${kpiData && kpiData.workRequestsChange >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                {kpiData ? `${kpiData.workRequestsChange >= 0 ? "+" : ""}${kpiData.workRequestsChange} this period` : ""}
              </p>
            </div>

            {/* KPI: Completed WOs Without Root Cause */}
            <div
              className="bg-white rounded-lg py-4 px-5 flex flex-col"
              style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
            >
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-medium text-[#6b7280]">Closed Tasks Without Root Cause</p>
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportNoRootCausePDF} disabled={isExportingNoRootCausePDF}>{isExportingNoRootCausePDF ? "Exporting..." : "PDF"}</Menu>
                    <Menu onClick={handleExportNoRootCauseXLS} disabled={isExportingNoRootCauseXLS}>{isExportingNoRootCauseXLS ? "Exporting..." : "XLS"}</Menu>
                  </Dropdown.Content>
                </Dropdown>
              </div>
              <div className="flex items-center mt-1 h-[56px]">
                <p className="text-[28px] font-bold text-[#111827] leading-tight">
                  {noRootCauseLoading ? "..." : noRootCauseCount !== null ? noRootCauseCount : "—"}
                </p>
              </div>
              <p className="text-[11px] text-[#6b7280] mt-auto pt-1">closed work orders (complete + incomplete) missing root cause</p>
            </div>

            {/* KPI: Total Tasks with Checklist Created */}
            <div
              className="bg-white rounded-lg py-4 px-5 flex flex-col"
              style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
            >
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-medium text-[#6b7280]">Total Tasks with Checklist Created</p>
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportChecklistTasksPDF} disabled={isExportingChecklistTasksPDF}>{isExportingChecklistTasksPDF ? "Exporting..." : "PDF"}</Menu>
                    <Menu onClick={handleExportChecklistTasksXLS} disabled={isExportingChecklistTasksXLS}>{isExportingChecklistTasksXLS ? "Exporting..." : "XLS"}</Menu>
                  </Dropdown.Content>
                </Dropdown>
              </div>
              <div className="flex items-center justify-between mt-1 h-[56px]">
                <p className="text-[28px] font-bold text-[#111827] leading-tight">
                  {kpiLoading ? "..." : kpiData ? kpiData.totalTasksWithChecklist.toLocaleString() : "—"}
                </p>
                <div className="w-[80px] h-[50px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={checklistSparklineData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                      <defs>
                        <linearGradient id="clGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#16a34a" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#16a34a" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke="#16a34a" strokeWidth={1.5} fill="url(#clGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <p className={`text-[11px] mt-auto pt-1 ${kpiData && kpiData.checklistChange >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                {kpiData ? `${kpiData.checklistChange >= 0 ? "+" : ""}${kpiData.checklistChange}% vs last period` : ""}
              </p>
            </div>

            {/* KPI: % of Completed Tasks with Checklists */}
            <div
              className="bg-white rounded-lg py-4 px-5 flex flex-col"
              style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
            >
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-medium text-[#6b7280]">% of Completed Tasks with Checklists</p>
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportChecklistCompletionPDF} disabled={isExportingChecklistCompletionPDF}>{isExportingChecklistCompletionPDF ? "Exporting..." : "PDF"}</Menu>
                    <Menu onClick={handleExportChecklistCompletionXLS} disabled={isExportingChecklistCompletionXLS}>{isExportingChecklistCompletionXLS ? "Exporting..." : "XLS"}</Menu>
                  </Dropdown.Content>
                </Dropdown>
              </div>
              <div className="flex items-center justify-between mt-1 h-[56px]">
                <p className="text-[28px] font-bold text-[#111827] leading-tight">
                  {kpiLoading ? "..." : kpiData ? `${kpiData.completedTasksWithChecklistPercentage}%` : "—"}
                </p>
                <div className="w-[56px] h-[56px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={kpiData ? [
                          { name: "With Checklist", value: kpiData.completedTasksWithChecklistPercentage, color: "#16a34a" },
                          { name: "Without Checklist", value: 100 - kpiData.completedTasksWithChecklistPercentage, color: "#e5e7eb" },
                        ] : donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={18}
                        outerRadius={27}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                      >
                        {(kpiData ? [
                          { color: "#16a34a" }, { color: "#e5e7eb" },
                        ] : donutData).map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <p className="text-[11px] text-[#6b7280] mt-auto pt-1">of completed tasks</p>
            </div>
          </div>

          {/* Charts row: Work Requests + Gallus (left) | Growers + WR Status (right) */}
          <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr] gap-5 mb-5">
            
              <ChartCard
                title="Origins of Work Requests"
                subtitle={`Cumulative — ${periodLabel}, by User Type`}
                rightHeader={
                  <Dropdown position="end">
                    <Dropdown.Button>
                      <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                        Export
                      </button>
                    </Dropdown.Button>
                    <Dropdown.Content>
                      <Menu onClick={handleExportPDF} disabled={isExportingPDF}>
                        {isExportingPDF ? "Exporting PDF..." : "PDF"}
                      </Menu>
                      <Menu onClick={handleExportXLS} disabled={isExportingXLS}>
                        {isExportingXLS ? "Exporting XLS..." : "Excel"}
                      </Menu>
                    </Dropdown.Content>
                  </Dropdown>
                }
              >
                {wrOriginLoading ? <ChartLoader /> : <div className="flex flex-col">
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart
                      data={workRequestData}
                      margin={{ top: 5, right: 10, left: -10, bottom: 15 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={<WRXAxisTick />} height={42} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip
                        labelFormatter={(label: string) => label.split("|")[0]}
                        contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                      />
                      {wrUserTypes.map((key, i) => (
                        <Bar key={key} dataKey={key} stackId="a" fill={wrColorMap[key]} radius={i === wrUserTypes.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center pt-2">
                    {wrUserTypes.map((name) => (
                      <div key={name} className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: wrColorMap[name] }} />
                        <span className="text-[11px] text-[#6b7280]">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>}
              </ChartCard>

              <ChartCard
                title="Origins of Tasks — Gallus"
                subtitle={`Cumulative — ${periodLabel}, by User`}
                rightHeader={
                  <Dropdown position="end">
                    <Dropdown.Button>
                      <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                        Export
                      </button>
                    </Dropdown.Button>
                    <Dropdown.Content>
                      <Menu onClick={handleExportGallusPDF} disabled={isExportingGallusPDF}>
                        {isExportingGallusPDF ? "Exporting PDF..." : "PDF"}
                      </Menu>
                      <Menu onClick={handleExportGallusXLS} disabled={isExportingGallusXLS}>
                        {isExportingGallusXLS ? "Exporting XLS..." : "Excel"}
                      </Menu>
                    </Dropdown.Content>
                  </Dropdown>
                }
              >
                {gallusLoading ? <ChartLoader /> : <div className="flex flex-col">
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={gallusTaskData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="role" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                        formatter={(value: any, name: any) => value > 0 ? [value, name] : null}
                        itemSorter={(item) => -(Number(item.value) || 0)}
                      />
                      {activeGallusUserNames.map((user, i) => (
                        <Bar
                          key={user}
                          dataKey={user}
                          stackId="a"
                          fill={gallusColorMap[user] || "#999"}
                          radius={i === activeGallusUserNames.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center pt-2">
                    {activeGallusUserNames.map((name) => (
                      <div key={name} className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: gallusColorMap[name] }} />
                        <span className="text-[11px] text-[#6b7280]">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>}
              </ChartCard>
            

            
            <ChartCard
              title="Origins of Work Requests — Grower"
              subtitle={`Cumulative — ${periodLabel}, by Grower`}
              rightHeader={
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                      Export
                    </button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportGrowerPDF} disabled={isExportingGrowerPDF}>
                      {isExportingGrowerPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportGrowerXLS} disabled={isExportingGrowerXLS}>
                      {isExportingGrowerXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              }
            >
              {facilityLoading ? <ChartLoader height={400} /> : <div className="flex flex-col" style={{ height: "100%" }}>
                <div className="flex-1 min-h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={growersTaskData}
                      layout="vertical"
                      margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis
                        type="category"
                        dataKey="facility"
                        tick={{ fontSize: 10, fill: "#6b7280" }}
                        width={135}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                        formatter={(value: any, name: any) => value > 0 ? [value, name] : null}
                        itemSorter={(item) => -(Number(item.value) || 0)}
                      />
                      {activeFacilityUsers.map((user, i) => (
                        <Bar
                          key={user}
                          dataKey={user}
                          stackId="a"
                          fill={facilityColorMap[user] || "#999"}
                          radius={i === activeFacilityUsers.length - 1 ? [0, 3, 3, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center pt-2">
                  {activeFacilityUsers.map((name) => (
                    <div key={name} className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: facilityColorMap[name] }} />
                      <span className="text-[11px] text-[#6b7280]">{name}</span>
                    </div>
                  ))}
                </div>
              </div>}
            </ChartCard>

            {/* WR Status Pie Chart */}
            <ChartCard
              title="Work Order Converted"
              subtitle={`WR status breakdown — ${periodLabel}`}
              rightHeader={
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                      Export
                    </button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportWRStatusPDF} disabled={isExportingWRStatusPDF}>
                      {isExportingWRStatusPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportWRStatusXLS} disabled={isExportingWRStatusXLS}>
                      {isExportingWRStatusXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              }
            >
              {wrStatusLoading ? <ChartLoader height={220} /> : (() => {
                const WR_STATUS_COLORS = ["#3b82f6", "#16a34a", "#ef4444"];
                const pieData = wrStatusData ? [
                  { name: "New", value: wrStatusData.wrNew },
                  { name: "Converted", value: wrStatusData.wrConverted },
                  { name: "Deleted", value: wrStatusData.wrDeleted },
                ].filter(d => d.value > 0) : [];
                const total = pieData.reduce((s, d) => s + d.value, 0);
                return (
                  <div className="flex flex-col items-center" style={{ height: 220 }}>
                    {pieData.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-[13px] text-[#9ca3af]">No data</div>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              dataKey="value"
                              startAngle={90}
                              endAngle={-270}
                              stroke="none"
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={index} fill={WR_STATUS_COLORS[index % WR_STATUS_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                              formatter={(value: any, name: any) => [`${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`, name]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-1">
                          {pieData.map((entry, index) => (
                            <div key={entry.name} className="flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: WR_STATUS_COLORS[index % WR_STATUS_COLORS.length] }} />
                              <span className="text-[11px] text-[#6b7280]">{entry.name}: {entry.value}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </ChartCard>
            
          </div>

          {/* User Activity Summary */}
          <div className="mb-5">
            <ChartCard
              title="User Activity Summary"
              subtitle={`Hours, observations & asset moves by user — ${periodLabel}`}
              rightHeader={
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                      Export
                    </button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportActivityPDF} disabled={isExportingActivityPDF}>
                      {isExportingActivityPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportActivityXLS} disabled={isExportingActivityXLS}>
                      {isExportingActivityXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              }
            >
              {activityLoading ? <ChartLoader height={400} /> : <div className="flex flex-col">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={activityData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="user" tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    />
                    {activityMetrics.map((m, i) => (
                      <Bar key={m.key} dataKey={m.key} stackId="a" fill={m.color} radius={i === activityMetrics.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
                  {activityMetrics.map((m) => (
                    <div key={m.key} className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: m.color }} />
                      <span className="text-[11px] text-[#6b7280]">{m.key}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-[11px] text-left">
                    <thead>
                      <tr className="border-b border-[#e5e7eb]">
                        <th className="py-2 px-2 font-semibold text-[#374151]">User</th>
                        {activityMetrics.map((m) => (
                          <th key={m.key} className="py-2 px-2 font-semibold text-[#374151] text-right">{m.key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activityData.map((row) => (
                        <tr key={row.user} className="border-b border-[#f3f4f6] hover:bg-[#f9fafb]">
                          <td className="py-1.5 px-2 font-medium text-[#111827]">{row.user}</td>
                          {activityMetrics.map((m) => (
                            <td key={m.key} className="py-1.5 px-2 text-right text-[#6b7280]">{row[m.key]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>}
            </ChartCard>
          </div>

          {/* WR Completeness By Facility */}
          <ChartCard
            title="Work Request Completeness by Farm"
            subtitle={`% of WRs missing key fields per farm — ${periodLabel}`}
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportWRCompletenessPDF} disabled={isExportingWRCompletenessPDF}>
                    {isExportingWRCompletenessPDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportWRCompletenessXLS} disabled={isExportingWRCompletenessXLS}>
                    {isExportingWRCompletenessXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            {wrCompletenessLoading ? <ChartLoader height={200} /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] bg-[#f9fafb]">
                      <th className="py-2 px-3 text-left font-semibold text-[#374151]">Farm</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">Total WRs</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">No Asset %</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">No Zone %</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">No Note %</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">No Sub-Facility %</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">No Picture %</th>
                      <th className="py-2 px-3 text-right font-semibold text-[#374151]">No Line %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wrCompletenessRawData.map((row: any) => {
                      const pct = (v: number) => v === 0 ? <span className="text-[#6b7280]">0%</span> : <span className={v >= 80 ? "text-red-600 font-semibold" : v >= 50 ? "text-orange-500 font-medium" : "text-[#374151]"}>{v}%</span>;
                      return (
                        <tr key={row.facilityName} className="border-b border-[#f3f4f6] hover:bg-[#f9fafb]">
                          <td className="py-1.5 px-3 font-medium text-[#111827]">{row.facilityName}</td>
                          <td className="py-1.5 px-3 text-right text-[#374151] font-medium">{row.totalWRs}</td>
                          <td className="py-1.5 px-3 text-right">{pct(row.pctNoAsset)}</td>
                          <td className="py-1.5 px-3 text-right">{pct(row.pctNoZone)}</td>
                          <td className="py-1.5 px-3 text-right">{pct(row.pctNoNote)}</td>
                          <td className="py-1.5 px-3 text-right">{pct(row.pctNoSubFacility)}</td>
                          <td className="py-1.5 px-3 text-right">{pct(row.pctNoPicture)}</td>
                          <td className="py-1.5 px-3 text-right">{pct(row.pctNoLine)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>

          {/* Stock Adjustments + Inventory Movement */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ChartCard
              title="Stock Adjustments"
              subtitle={`Manual inventory corrections — ${periodLabel}`}
              rightHeader={
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                      Export
                    </button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportStockPDF} disabled={isExportingStockPDF}>
                      {isExportingStockPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportStockXLS} disabled={isExportingStockXLS}>
                      {isExportingStockXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              }
            >
              {stockAdjLoading ? <ChartLoader /> : <>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={stockAdjData}
                  margin={{ top: 5, right: 10, left: -10, bottom: 15 }}
                  barCategoryGap="20%"
                  barGap={3}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={<WRXAxisTick />} height={42} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v: any) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", padding: "8px 12px" }}>
                          <p style={{ fontWeight: 600, marginBottom: 4 }}>{(label as string)?.split("|")[0]}</p>
                          <p style={{ color: "#16a34a" }}>▲ Positive: ${Number(d.positiveDollars).toLocaleString()} ({d.positiveCount} adjustment{d.positiveCount !== 1 ? 's' : ''})</p>
                          <p style={{ color: "#dc2626" }}>▼ Negative: ${Number(d.negativeDollars).toLocaleString()} ({d.negativeCount} adjustment{d.negativeCount !== 1 ? 's' : ''})</p>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    iconType="square"
                    iconSize={10}
                    wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  />
                  <Bar dataKey="positiveDollars" name="Positive" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="negativeDollars" name="Negative" fill="#dc2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-between px-3 py-2 mt-1 bg-gray-50 rounded-md border border-gray-200">
                <span className="text-[0.75rem] font-medium text-slate-500">Total Adjustment Amount</span>
                <span className="text-[0.8rem] font-bold text-slate-800">
                  ${stockAdjData.reduce((sum: number, r: any) => sum + (r.positiveDollars || 0) - (r.negativeDollars || 0), 0).toLocaleString()}
                </span>
              </div>
              </>}
            </ChartCard>

            <ChartCard
              title="Inventory Movement"
              subtitle={`Stock picking, returning and using — ${periodLabel}`}
              rightHeader={
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                      Export
                    </button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportInvPDF} disabled={isExportingInvPDF}>
                      {isExportingInvPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportInvXLS} disabled={isExportingInvXLS}>
                      {isExportingInvXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              }
            >
              {invMovementLoading ? <ChartLoader /> : <div className="flex flex-col">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={stockPickData}
                    margin={{ top: 20, right: 10, left: -10, bottom: 15 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={<WRXAxisTick />} height={42} />
                    <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <Tooltip content={<StockPickingTooltip />} />
                    {Object.entries(stockPickingColors).map(([key, color]) => (
                      <Bar key={key} dataKey={key} fill={color} radius={[3, 3, 0, 0]}>
                        <LabelList
                          dataKey={`${key.toLowerCase()}Dollars`}
                          position="top"
                          fontSize={9}
                          fill="#374151"
                          formatter={(v: any) => `$${Number(v).toLocaleString()}`}
                        />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
                  {Object.entries(stockPickingColors).map(([name, color]) => (
                    <div key={name} className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-[11px] text-[#6b7280]">{name}</span>
                    </div>
                  ))}
                </div>
              </div>}
            </ChartCard>
          </div>

          <div className="mt-5">
            <ChartCard
              title="Checklists by Creator"
              subtitle={`Number of checklists created per user — ${periodLabel}`}
              rightHeader={
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                      Export
                    </button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportChecklistCreatorPDF} disabled={isExportingChecklistCreatorPDF}>
                      {isExportingChecklistCreatorPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportChecklistCreatorXLS} disabled={isExportingChecklistCreatorXLS}>
                      {isExportingChecklistCreatorXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              }
            >
              {checklistByCreatorLoading ? <ChartLoader /> : checklistByCreatorData.length === 0 ? (
                <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No checklists created in this period</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={checklistByCreatorData}
                    margin={{ top: 5, right: 10, left: -10, bottom: 55 }}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="creatorName"
                      tick={{ fontSize: 11, fill: "#6b7280" }}
                      angle={-35}
                      textAnchor="end"
                      interval={0}
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                      formatter={(value: any) => [value, "Checklists Created"]}
                    />
                    <Bar dataKey="checklistCount" name="Checklists Created" fill="#7c3aed" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="checklistCount" position="top" style={{ fontSize: 11, fill: "#374151", fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdoptionDashboard;
